import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  Calendar,
  Users,
  TrendingUp,
  TrendingDown,
  Building,
  Plane
} from 'lucide-react';

const ExpenseCard = ({ campaign, onSelect, totalExpenses }) => {
  const { t } = useTranslation();

  const getTypeIcon = (type) => {
    return type === 'sold' ? Building : Plane;
  };

  const getTypeColor = (type) => {
    return type === 'sold' 
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  const getExpenseCount = () => {
    return Object.keys(campaign.expenses || {}).filter(key => campaign.expenses[key] > 0).length;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const profitLoss = campaign.totalRevenue - campaign.totalCosts;
  const isProfitable = profitLoss >= 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
              {campaign.campaignName}
            </h3>
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(campaign.campaignType)}`}>
                {React.createElement(getTypeIcon(campaign.campaignType), { className: "h-3 w-3 mr-1" })}
                {t(`campaigns.${campaign.campaignType}`)}
              </span>
            </div>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Calendar className="h-4 w-4 mr-2" />
            <span>
              {formatDate(campaign.departureDate)} - {formatDate(campaign.returnDate)}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Users className="h-4 w-4 mr-2" />
            <span>{campaign.maxCapacity} {t('campaigns.travelers')}</span>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('expenses.totalExpenses')}
            </span>
            <span className="font-semibold text-red-600 dark:text-red-400">
              {totalExpenses.toLocaleString()} {t('common:currency')}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('campaigns.totalRevenue')}
            </span>
            <span className="font-semibold text-green-600 dark:text-green-400">
              {campaign.totalRevenue.toLocaleString()} {t('common:currency')}
            </span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {t('campaigns.netProfit')}
            </span>
            <div className="flex items-center">
              {isProfitable ? (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={`font-semibold ${
                isProfitable 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {Math.abs(profitLoss).toLocaleString()} {t('common:currency')}
              </span>
            </div>
          </div>
        </div>

        {/* Expense Categories Count */}
        <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('expenses.expenseCategories')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {getExpenseCount()} {t('expenses.categories')}
          </span>
        </div>

        {/* Sold Campaign Specific Info */}
        {campaign.campaignType === 'sold' && (
          <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-sm text-purple-800 dark:text-purple-200">
              <div className="flex justify-between mb-1">
                <span>{t('campaigns.costPrice')}:</span>
                <span className="font-semibold">{campaign.costPrice?.toLocaleString()} {t('common:currency')}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>{t('campaigns.sellingPrice')}:</span>
                <span className="font-semibold">{campaign.sellingPrice?.toLocaleString()} {t('common:currency')}</span>
              </div>
              <div className="text-xs mt-2 text-purple-600 dark:text-purple-300">
                {t('campaigns.buyer')}: {campaign.buyerCompany}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onSelect}
            className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            <Eye className="h-4 w-4 mr-2" />
            {t('expenses.viewExpenses')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCard;